import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { format, parseISO, isBefore } from "date-fns";

import {
  CONSULTATION_TYPE_INSOMNIA,
  CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_REFILL,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_REFILL,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  FILL_REQUEST_STATUS_NEW,
  FILL_REQUEST_STATUS_SENT,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  FILL_REQUEST_STATUS_FILLED,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_PROCESSING,
  FILL_REQUEST_STATUS_PROCESSING_DONE,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_INACTIVE,
  FILL_REQUEST_STATUS_MATCHING_PENDING,
  FILL_REQUEST_STATUS_PAYMENT_PENDING,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY,
  FILL_PHARMACY_ID_HONEYBEE_CA,
  TANGERINE_FLAVOR,
  SPEARMINT_FLAVOR,
  FRUIT_PUNCH_FLAVOR,
  FLAVOR_NAME_FRUIT_PUNCH,
  FLAVOR_NAME_SPEARMINT,
  FLAVOR_NAME_TANGERINE,
} from "../sharedConstants";
// -------------------------------------------------------------------

import imgTwoPack from "~assets/images/visits/two-pack.png";
import imgOnePack from "~assets/images/visits/one-pack.png";
import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";

export function isDevelopmentEnvironment() {
  return process.env.NODE_ENV === "development";
}

// isPharmacyPickupOrderType returns true if the orderType is a pharmacy pickup order type
export function isPharmacyPickupOrderType(orderType) {
  return orderType?.includes("visit-only");
}

export function isMailOrderOrderType(orderType) {
  return !isPharmacyPickupOrderType(orderType);
}

// isVisitOnlyBBOrderType
export function isVisitOnlyBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_VISIT_ONLY ||
    orderType == ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_2_PACK ||
    orderType == ORDER_TYPE_PROP_2_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK ||
    orderType == ORDER_TYPE_PROP_1_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isFlavoredBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isBBOrderType(orderType) {
  return isVisitOnlyBBOrderType(orderType) || isMailOrderBBOrderType(orderType);
}

export function isScopolamineAddonOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY
  );
}

export function isPharmacyPickupSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP
  );
}

export function isMailOrderType(orderType) {
  return (
    isMailOrderBBOrderType(orderType) || isMailOrderSleepOrderType(orderType)
  );
}

export function isSleepOrderType(orderType) {
  return (
    isPharmacyPickupSleepOrderType(orderType) ||
    isMailOrderSleepOrderType(orderType)
  );
}

// isPharmacyPickupFromVisit returns true if the differentPharmacyRawCookie is not empty
export function isPharmacyPickupFromVisit(differentPharmacyRawCookie) {
  // console.log("rawCookie: ", differentPharmacyRawCookie);
  // console.log(differentPharmacyRawCookie != `""`);
  return differentPharmacyRawCookie && differentPharmacyRawCookie != `""`;
}

export function isVisitTypeInsomnia(visitType) {
  return (
    visitType == VISIT_TYPE_INSOMNIA ||
    visitType == VISIT_TYPE_INSOMNIA_FOLLOW_UP
  );
}

export function isVisitTypePerformanceAnxiety(visitType) {
  return (
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY ||
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}

export function isConsultationTypePerformanceAnxietyInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY
  );
}
export function isConsultationTypePerformanceAnxietyFollowUp(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType ==
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}
export function isConsultationTypeInsomniaInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_INSOMNIA ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_INSOMNIA
  );
}

export function isConsultationTypeInsomniaFollowUp(consultationType) {
  return [
    CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  ].includes(consultationType?.toLowerCase());
}

export function isConsultationTypeInsomnia(consultationType) {
  return (
    isConsultationTypeInsomniaInitial(consultationType) ||
    isConsultationTypeInsomniaFollowUp(consultationType)
  );
}

// -------------------------------------------------------------------

export function refillOrderTypeFor(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_REFILL;
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_REFILL;
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return ORDER_TYPE_PROP_VISIT_ONLY;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED;
  }
  return "";
}

export function followUpOrderTypeForRefillType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP;
    default:
      return orderType;
  }
}

// isOriginalOrFollowUpVisit returns true if the orderType on a doctorVisit is not a refill order
export function isOriginalOrFollowUpVisit(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return true;
  }
  return false;
}

export function getDrugQuantityForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return 48;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return 24;

    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return 4;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return 10;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return 6;
  }
}

export function getNumRefillsForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return 3;

    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return 1;
  }

  return 1;
}

// isDoctorVisitComplete returns true if a doctor visit has status of Closed Complete ('CC')
export function isDoctorVisitComplete(status) {
  return status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE;
}

// hasRefillsRemaining returns true if a doctorPrescription has refills available
export function hasRefillsAvailable(refills, numRefillsFilled) {
  return refills - numRefillsFilled > 0;
}

export function numRefillsRemaining(refills, numRefillsFilled) {
  let numRemaining = refills - numRefillsFilled;
  return numRemaining < 0 ? 0 : numRemaining;
}

// displayFriendlyLongDate returns date in "October 15 2020" format
export function displayFriendlyLongDate(date) {
  return format(new Date(date), "MMMM dd yyyy");
}

export function displayFriendlyDate(date) {
  return format(new Date(date), "MMM d, yyyy");
}

// displayISOShortDate returns date in "1/2/21" format, and returns blank if date is empty
export function displayISOShortDate(date) {
  const shortDate = parseISO(date);

  return isBefore(shortDate, new Date(0, 0, 1))
    ? ""
    : format(shortDate, "M/d/yy");
}

// displayFriendlyPrescriptionExpirationLongDate adds 360 days to the original prescription visit date {
export function displayFriendlyPrescriptionExpirationLongDate(date) {
  var result = new Date(date);
  result.setDate(result.getDate() + 362);

  return format(result, "MMMM dd yyyy");
}

export function displayFriendlyShortenedOrderType(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "2 Pack";
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "1 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Pharmacy Pick-Up";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return `2 Pack ${getFlavorName(subcategory) || "Flavored"}`;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return `1 Pack ${getFlavorName(subcategory) || "Flavored"}`;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Sample Pack ${getFlavorName(subcategory) || "Flavored"}`;
  }
  return "";
}

export function getOrderTypeImageSrc(productSlug, subcategory) {
  switch (productSlug) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return imgTwoPack;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return imgOnePack;

    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return imgSleep;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP: {
      switch (subcategory) {
        case TANGERINE_FLAVOR:
          return imgTangerine;
        case SPEARMINT_FLAVOR:
          return imgSpearmint;
        case FRUIT_PUNCH_FLAVOR:
          return imgFruitPunch;
        default:
          return imgSpearmint;
      }
    }
  }

  return imgOnePack;
}

export function getVisitTypeImageSrc(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return imgOnePack;

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return imgSleep;
  }
  return imgOnePack;
}

export function getConsultationNameFromVisitType(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety";

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return "Insomnia";
  }
}

export function getConsultationNameFromType(consultationType) {
  switch (consultationType) {
    // Performance Anxiety
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      return "Performance Anxiety";
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety Follow-Up";
    case CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON:
      return "Scopolamine Addon";
    // Insomnia
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      return "Insomnia";
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
      return "Insomnia Follow-Up";
  }
}

export function latestVisitCompletedAtOrVisitStartedAt(visitDate, completedAt) {
  return completedAt > visitDate ? completedAt : visitDate;
}

export function mapFillRequestStatusToUserFriendlyStatus(status) {
  switch (status) {
    case FILL_REQUEST_STATUS_NEW:
      return "New";
    case FILL_REQUEST_STATUS_SENT:
      return "Sent";
    case FILL_REQUEST_STATUS_SENDING_ERROR:
      return "Send Error";
    case FILL_REQUEST_STATUS_FILLED:
      return "Filled";
    case FILL_REQUEST_STATUS_ERROR:
      return "Error";
    case FILL_REQUEST_STATUS_CANCELLED:
      return "Cancelled";
    case FILL_REQUEST_STATUS_PROCESSING:
      return "Processing";
    case FILL_REQUEST_STATUS_PROCESSING_DONE:
      return "Processing Done";
    case FILL_REQUEST_STATUS_DELETED:
      return "Deleted";
    case FILL_REQUEST_STATUS_INACTIVE:
      return "Inactive";
    case FILL_REQUEST_STATUS_MATCHING_PENDING:
      return "Pending RX";
    case FILL_REQUEST_STATUS_PAYMENT_PENDING:
      return "Pending Payment";
    case FILL_REQUEST_STATUS_MATCH_ERROR:
      return "RX Match Error";
    case FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE:
      return "Payment Uncollectible";
  }

  return status;
}

export async function copyTextToClipboard(copyText) {
  navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
    console.log("clipboard access state: ", result.state);
    if (result.state == "granted" || result.state == "prompt") {
      try {
        navigator.clipboard.writeText(copyText);
        console.log("Copied to clipboard: ", copyText);
        /* Resolved - text copied to clipboard successfully */
      } catch (err) {
        console.error("Failed to copy: ", err);
        /* Rejected - text failed to copy to the clipboard */
      }
    } else {
      alert(`Clipboard access denied: ${copyText}`);
    }
  });
}

export const getGoogleAPIKey = () => {
  return (
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "AIzaSyB-Lywz9aSHxSHQF23lrJJMKG3I75H2DmE"
  );
};

export const getZerobounceAPIKey = () => {
  return (
    process.env.REACT_APP_ZEROBOUNCE_API_KEY ||
    "84ed5171801b4eb7b70fc3a41f2bbe33"
  );
};

export function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return null;
}

export function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export const supportedUSStates = [
  "AL",
  "CA",
  "CO",
  "CT",
  "FL",
  "GA",
  "HI",
  "IA",
  "IL",
  "KS",
  "KY",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NY",
  "OH",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "WA",
  "WI",
  "WY",
];

export const controlledDrugsByState = {
  gabapentin: ["AL", "KY", "ND", "TN", "VA", "UT", "WV"],
  cyclobenzaprine: ["MN"],
};

export const pdmpDrugsByState = {
  gabapentin: [
    "CT",
    "DC",
    "IN",
    "KS",
    "MA",
    "MN",
    "NC",
    "NE",
    "NJ",
    "OH",
    "OR",
    "WI",
    "WY",
  ],
  cyclobenzaprine: ["DC", "WY"],
};

export const isPDMPSleepMedication = (usState, medicationName) => {
  return (pdmpDrugsByState[medicationName] || []).includes(usState);
};

export const isStateControlledMedication = (usState, medicationName) => {
  return (controlledDrugsByState[medicationName] || []).includes(usState);
};

export const nullUUID = "00000000-0000-0000-0000-000000000000";

export const isNullUUID = (uuid) => {
  return uuid === nullUUID;
};

export const isNullDate = (date) => {
  return (
    date === null ||
    date === undefined ||
    date === "" ||
    date === "0001-01-01T00:00:00Z"
  );
};

export const addressValidationSchema = Yup.object().shape({
  address1: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  usState: Yup.string().required("Required"),
  zip: Yup.string().required("Required"),
});

export const getConsultationTypeByProduct = (product) => {
  switch (product) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
  }
};

export function anyOrderItemsAreMailOrder(orderItems) {
  return orderItems.some(
    (orderItem) => !isPharmacyPickupOrderType(orderItem.productSlug)
  );
}

export function anyOrderItemsArePharmacyPickup(orderItems) {
  return orderItems.some((orderItem) =>
    isPharmacyPickupOrderType(orderItem.productSlug)
  );
}

export function getAllMedicationsFromVisits(visits) {
  const medications = [];
  visits.forEach((visit) => {
    (visit.visitPrescriptions || []).forEach(
      ({ medicationName, medicationDosage }) => {
        medications.push({
          medicationName,
          medicationDosage,
        });
      }
    );
  });
  return medications;
}

/*
  Get medications to check if patient has ever been prescribed 20mg propranolol or atenolol.
  If doctor is prescribing 10mg propranolol and patient has received 20mg prop or atenolol in the past,
  then show a confirm.
*/
export function verifyNoteWithPrevMeds(note, meds) {
  const has20mgPropInPast = meds.some(
    ({ medicationName, medicationDosage }) =>
      medicationDosage === "20mg" &&
      medicationName.toLowerCase() === "propranolol"
  );
  const hasAtenololInPast = meds.some(
    ({ medicationName }) => medicationName.toLowerCase() === "atenolol"
  );

  if (
    note.includes("propranolol") &&
    !note.includes("20mg") &&
    has20mgPropInPast
  ) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Propranolol 20mg in the past. Continue with 10mg prescription?"
      )
    ) {
      return false;
    }
  } else if (note.includes("propranolol") && hasAtenololInPast) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Atenolol 25mg in the past. Continue with Propranolol prescription?"
      )
    ) {
      return false;
    }
  }

  return true;
}

export function getOptionsForEditOrderType(orderType) {
  // Sleep
  if (isSleepOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
      ];
    }
  }

  // Flavored BBs
  if (isFlavoredBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
      ];
    } else {
      return [ORDER_TYPE_PROP_1_PACK_FLAVORED, ORDER_TYPE_PROP_2_PACK_FLAVORED];
    }
  }

  // BBs
  if (isBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [ORDER_TYPE_PROP_1_PACK, ORDER_TYPE_PROP_2_PACK];
    }
  }

  // Scopolamine Addon
  if (isScopolamineAddonOrderType(orderType)) {
    return [
      ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
      ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
    ];
  }
}

export function isFollowUpConsultationType(consultationType) {
  return (
    consultationType ===
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType === CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP
  );
}

export function isFollowUpOrderType(orderType) {
  return orderType.includes("follow-up");
}

export function displayOrderType(visitOrderType) {
  switch (visitOrderType) {
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return "Performance Anxiety - Pickup";
    case ORDER_TYPE_PROP_1_PACK:
      return "Performance Anxiety - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
      return "Performance Anxiety - 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Performance Anxiety - Pickup (Follow Up)";
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Performance Anxiety - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Performance Anxiety - 2 Pack (Follow Up)";

    // Flavored
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return "Performance Anxiety Flavored - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return "Performance Anxiety Flavored - 2 Pack";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored - 2 Pack (Follow Up)";
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return "Performance Anxiety Flavored - Sample Pack";

    // Sleep
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return "Insomnia - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return "Insomnia - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return "Insomnia - 6 Month Semi-Annual";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
      return "Insomnia - Follow Up";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return "Insomnia Pickup - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return "Insomnia Pickup - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return "Insomnia Pickup - 6 Month Semi-Annual";

    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return "Insomnia Pickup - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS:
      return "Insomnia Pickup - 2 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return "Insomnia - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS:
      return "Insomnia - 2 Month Trial";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return "Insomnia Pickup - Follow-Up";

    // Scopolamine Addon
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 4 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 10 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return "Scopolamine Addon - 4 Pack (Local Pickup)";

    default:
      return `{UNKNOWN VISIT ORDER TYPE: ${visitOrderType}}`;
  }
}

export function displayOrderTypePacks(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Beta Blockers 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Beta Blockers 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Beta Blockers Pickup (Visit Only)";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 1 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 2 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Beta Blockers Flavored Sample Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
  }
  return "";
}

export function generatePharmacyOrderLinkURL(pharmacyID, orderNumberOrFillID) {
  switch (pharmacyID) {
    case FILL_PHARMACY_ID_HONEYBEE_CA:
      return `https://partners.honeybeehealth.com/orders/${orderNumberOrFillID}`;
    default:
      return "";
  }
}

export function titleCase(str) {
  return str.replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

const compoundedOrderTypes = [
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
];

export function isCompoundedOrderType(orderType) {
  return compoundedOrderTypes.includes(orderType);
}

const scriptsureDrugNameMap = {
  PROPRANOLOL_10MG_FLAVORED: "Propranolol HCL 10mg",
  PROPRANOLOL_20MG_FLAVORED: "Propranolol HCL 20mg",
  ATENOLOL_25MG_FLAVORED: "Atenolol 25mg",
  DOXEPIN_3MG_GUMMY: "Doxepin 3mg",
};

function getScriptsureDrugNamePrefix(productSlug) {
  if (productSlug.includes("1-pack")) {
    return "1 Pack";
  } else if (productSlug.includes("2-pack")) {
    return "2 Pack";
  } else if (productSlug.includes("trial")) {
    return "Sample Pack";
  }
  return "";
}

function createScriptsureDrugNameFromMedNameKey(medName) {
  const [medConstant, quantity] = medName.split("-");
  return `${scriptsureDrugNameMap[medConstant]} x${quantity}`;
}

export function getScriptsureDrugName(orderItem = {}, medName) {
  const { subcategory, productSlug = "" } = orderItem;

  if (!productSlug) {
    return createScriptsureDrugNameFromMedNameKey(medName);
  }

  // scriptsureDrugNamePrefix denotes the quantity (1 pack, 2 pack, or sample)
  const scriptsureDrugNamePrefix = getScriptsureDrugNamePrefix(productSlug);
  // scriptsureDrugName denotes the drug name and dosage
  const scriptsureDrugName = scriptsureDrugNameMap[medName];
  // subcategory denotes the flavor
  return `${scriptsureDrugNamePrefix} ${scriptsureDrugName} ${
    getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
  }`.trim();
}

export const supportedUSStatesForCompoundedMeds = [
  "CA",
  "CO",
  "FL",
  "GA",
  "HI",
  "IA",
  "IL",
  "KY",
  "MA",
  "MI",
  "NE",
  "NH",
  "NY",
  "OR",
  "PA",
  "RI",
  "SD",
  "TX",
  "VA",
  "WI",
];

export function getFlavorName(flavor = "") {
  switch (flavor) {
    case TANGERINE_FLAVOR:
      return FLAVOR_NAME_TANGERINE;
    case SPEARMINT_FLAVOR:
      return FLAVOR_NAME_SPEARMINT;
    case FRUIT_PUNCH_FLAVOR:
      return FLAVOR_NAME_FRUIT_PUNCH;
    default:
      return titleCase(flavor);
  }
}

export function isCompoundedMedication(medicationConstant) {
  return (
    medicationConstant.includes("FLAVORED") ||
    medicationConstant.endsWith("GUMMY")
  );
}
